import * as d3 from 'd3';
import ceil from 'lodash-es/ceil';

// the max number of characters to allow for the labels
const maxLength = 15;
const labelPadding = {
	xAxis: 20,
	yAxis: 10,
};

// overall margins for the main chart
const margin = {
	top: 7,
	right: 30,
	bottom: 0, // gets set during init and update
	left: 50 + labelPadding.yAxis,
};

/**
 * @param svg
 * @returns {{clear: clear, update: externalUpdate}}
 * @constructor
 */
export const LineChart = (svg) => {
	let initialized = false;

	// y scale for main chart
	const yScale = d3.scaleLinear();
	const yAxis = d3.axisLeft(yScale).ticks(10, '~s');

	// x scale for main chart
	const xScale = d3.scalePoint().padding(0.25);
	const xAxis = d3.axisBottom(xScale);

	const lineRenderer = d3
		.line()
		.curve(d3.curveCardinal.tension(0.5))
		.x((d) => xScale(d.x))
		.y((d) => yScale(d.y));

	const getLongestValue = (entries) => {
		let max = 0;
		entries.forEach((entry) => {
			if (entry.length > max) {
				if (entry.length > maxLength) max = maxLength;
				else max = entry.length;
			}
		});
		let adjustment = max * 6.5 + labelPadding.xAxis;
		margin.bottom = adjustment > maxLength * 6.5 ? maxLength * 6.5 : adjustment;
	};

	// manage the initialization of the chart, should only be called once
	function init({ extent, lineValues }, { height, width }, {yAxisLabel="Count"}) {
		getLongestValue(lineValues);

		// create group for grid lines first
		d3.select(svg).append('g').classed('gridGroup', true);

		// update the scales
		yScale.domain([0, nextIncrement(extent[1])]).range([height - margin.bottom, margin.top]);
		xScale.domain(lineValues).range([margin.left, width - margin.right]);

		// create yAxis
		d3.select(svg)
			.append('g')
			.classed('yAxis', true)
			.attr('transform', `translate(${margin.left},0)`)
			.attr('x', 0 - margin.bottom)
			.call(yAxis);

		// create yAxis Label
		d3.select(svg)
			.append('text')
			.classed('yAxisLabel', true)
			.attr('transform', 'rotate(-90)')
			.attr('y', 10)
			.attr('x', 0 - height / 2 + 15)
			.attr('dy', '1em')
			.attr('dx', '1em')
			.style('text-anchor', 'middle')
			.text(yAxisLabel);

		// create xAxis
		d3.select(svg)
			.append('g')
			.classed('xAxis', true)
			.attr('transform', `translate(0,${yScale(0)})`)
			.call(xAxis);

		// create main chart bar group
		d3.select(svg)
			.append('g')
			.classed('lineGroup', true)
			.attr('fill', 'none')
			.attr('stroke-width', 1.5)
			.attr('stroke-linejoin', 'round')
			.attr('stroke-linecap', 'round');

		// create main point group
		d3.select(svg).append('g').classed('pointGroup', true);
	}

	function update({ lineValues, lines, extent, colorPropertyMap }, { height, width, hideLabels }, { colorScale }) {
		getLongestValue(lineValues);
		const _margin = {...margin};


		if(hideLabels) {
			_margin.left = 5;
			_margin.bottom = 5; //reset on rerender so don't need to cache it
		}
		else {
			_margin.left = margin.left;
		}


		// update the scales
		yScale.domain([0, nextIncrement(extent[1])]).range([height - _margin.bottom, _margin.top]);
		xScale.domain(lineValues).range([_margin.left, width - _margin.right]);

		xAxis.tickFormat((_, i) => truncString(xScale.domain()[i], _margin.bottom));

		// update y axis
		d3.select(svg)
			.select('g.yAxis')
			.attr('transform', `translate(${_margin.left},0)`)
			.transition('yAxis')
			.duration(150)
			.call(yAxis);

		// update x axis
		d3.select(svg)
			.select('g.xAxis')
			.attr('transform', `translate(0,${yScale(0)})`)
			.transition('xAxis')
			.duration(150)
			.call(xAxis)
			.selectAll('text')
			.attr('transform', 'rotate(-65)')
			.style('text-anchor', 'end')
			.attr('dx', '-.8em')
			.attr('dy', '-.15em');

		d3.select(svg)
			.select('.yAxisLabel')
			.attr('x', 0 - height / 2 + 15);

		// add the Y gridlines
		d3.select(svg).selectAll('.grid').remove();
		d3.select(svg)
			.select('.gridGroup')
			.append('g')
			.attr('class', 'grid y-grid')
			.attr('transform', `translate(${_margin.left}, 0)`)
			.call(
				d3
					.axisLeft(yScale)
					.tickSize(-width + _margin.right + _margin.left)
					.tickFormat('')
			);

		// add all the lines
		d3.select(svg)
			.select('.lineGroup')
			.selectAll('path')
			.data(Object.entries(lines), (d) => d[0])
			.join('path')
			.style('mix-blend-mode', 'multiply')
			.style('opacity', 1)
			.style('stroke', (d) => colorScale(colorPropertyMap[d[0]]))
			.attr('d', (d) => {
				return lineRenderer(d[1]);
			});

		// add all the points
		d3.select(svg)
			.select('.pointGroup')
			.selectAll('.points')
			.data(Object.entries(lines), (d) => d[0])
			.join(
				(enter) => enter.append('g').classed('points', true),
				(update) => update,
				(exit) => exit.remove()
			)
			.selectAll('.point')
			.data((d) => {
				return d[1].map((item) => {
					return {
						...item,
						label: d[0],
					};
				});
			})
			.join(
				(enter) =>
					enter
						.append('circle')
						.classed('point', true)
						.attr('r', 4)
						.attr('stroke', 'white')
						.style('opacity', 1)
						.attr('fill', (d) => colorScale(colorPropertyMap[d.label]))
						.attr('cx', (d) => xScale(d.x))
						.attr('cy', (d) => yScale(d.y)),
				(update) =>
					update
						.attr('fill', (d) => colorScale(colorPropertyMap[d.label]))
						.attr('cx', (d) => xScale(d.x))
						.style('opacity', 1)
						.attr('cy', (d) => yScale(d.y)),
				(exit) => exit.remove()
			);

		d3.select(svg)
			.selectAll(".yAxis,.yAxisLabel,.xAxis")
			.style("display", hideLabels ? "none" : null);
	}
	const unfocusedLineOpacity = .15
	function mouseOver(key) {
		d3.select(svg)
			.select('.lineGroup')
			.selectAll('path')
			.transition()
			.style('opacity', (d)=> {
				return d[0]===key?1.0:unfocusedLineOpacity
			})
		d3.select(svg)
			.select('.pointGroup')
			.selectAll('.points')
			.selectAll('.point')
			.transition()
			.style('opacity', (d)=> {
				return d["label"]===key?1.0:unfocusedLineOpacity
			})
	}

	function mouseOut() {
		d3.select(svg)
			.select('.lineGroup')
			.selectAll('path')
			.transition()
			.style('opacity', 1)
		d3.select(svg)
			.select('.pointGroup')
			.selectAll('.points')
			.selectAll('.point')
			.transition()
			.style('opacity', 1)
	}

	function externalUpdate(data, dimensions, options) {
		if (!initialized) {
			init(data, dimensions, options);
			initialized = true;
		}
		update(data, dimensions, options);
	}

	function clear() {
		d3.select(svg).selectAll('g').remove();
	}

	return {
		update: externalUpdate,
		clear: clear,
		mouseOver: mouseOver,
		mouseOut: mouseOut,
	};
};

/**
 * Determines a reasonable choice for the next increment to show.
 *
 * This helps when you want to have some padding on the max extent of chart values.
 *
 * @param i
 * @returns {number}
 */
export function nextIncrement(i) {
	const increment = order(i);

	if (increment === 1) {
		return 10;
	} else if (i < 50) {
		return i % 2 === 0 ? i + 2 : i + 3;
	} else if (increment === 10 || increment === 100) {
		return ceil(i, -1);
	}

	const digits = increment.toString().length;
	return ceil(i, -(digits - 2));
}

/**
 * Determines the order of magnitude of the given value.
 *
 * @param n
 * @returns {number}
 */
export function order(n) {
	const order = Math.floor(Math.log(n) / Math.LN10 + 0.000000001); // because float math sucks like that
	return Math.pow(10, order);
}

/**
 * Hard truncates a string adding '...' or the given add
 * @param str
 * @param max
 * @param add
 * @returns {string|*}
 */
export function truncString(str, max, add) {
	add = add || '...';
	return typeof str === 'string' && str.length > max + add.length ? str.substring(0, max) + add : str;
}
